'use client';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef } from 'react';

import {
  AssetType,
  CategoryType,
  CuratedAssetType,
  CuratedCategoryType,
  getAssets,
  getCategories,
} from '@vimond/services/content-discovery';
import {
  addAssetToWatchlist,
  addCategoryToWatchlist,
  getAssetWatchlist,
  getCategoryWatchlist,
  removeAssetFromWatchlist,
  removeCategoryFromWatchlist,
} from '@vimond/services/playlist';
import { useAuthentication } from '../../authentication';
import { useSubprofiles } from '../../subprofiles';

type UseWatchlist = {
  assets: AssetType[] | undefined;
  categories: CategoryType[] | undefined;
  isLoading: boolean;
  isInWatchlist: ({
    assetId,
    categoryId,
  }: {
    assetId?: string;
    categoryId?: string;
  }) => boolean;
  addToWatchlist: (variables: {
    asset?: AssetType | CuratedAssetType;
    category?: CategoryType | CuratedCategoryType;
  }) => Promise<void | object>;
  removeFromWatchlist: (variables: {
    asset?: AssetType | CuratedAssetType;
    category?: CategoryType | CuratedCategoryType;
  }) => Promise<void>;
  addToWatchlistStatus: 'idle' | 'loading' | 'success' | 'error' | 'pending';
  removeFromWatchlistStatus:
    | 'idle'
    | 'loading'
    | 'success'
    | 'error'
    | 'pending';
};

const PENDING_ASSET_OPERATIONS = 'pendingAssetOperations';
const PENDING_CATEGORY_OPERATIONS = 'pendingCategoryOperations';
const OPERATION_TIMEOUT = 30 * 1000;

export const useWatchlist = (locale?: string): UseWatchlist => {
  const { jwt } = useAuthentication();
  const { subprofileToken } = useSubprofiles();
  const subProfileTokenRef = useRef<string | undefined>(subprofileToken);

  const assetsCacheKey = useMemo(() => ['watchlist', 'assets'], []);
  const categoriesCacheKey = useMemo(() => ['watchlist', 'categories'], []);

  const queryClient = useQueryClient();

  const { data: assets, isLoading: assetsAreLoading } = useQuery({
    queryKey: assetsCacheKey,
    queryFn: async () => {
      if (!jwt || !subprofileToken) return [];
      const watchList = await getAssetWatchlist({ jwt, subprofileToken });
      const assetIds = watchList?.assetIds;
      if (!assetIds) return [];
      const fetchedAssets = await getAssets({ assetIds, locale });
      return fetchedAssets ?? [];
    },
    enabled: !!jwt && !!subprofileToken,
  });

  const { data: categories, isLoading: categoriesAreLoading } = useQuery({
    queryKey: categoriesCacheKey,
    queryFn: async () => {
      if (!jwt || !subprofileToken) return [];
      const watchList = await getCategoryWatchlist({ jwt, subprofileToken });
      const categoryIds = watchList?.assetIds; // backend only supports assetIds and not categoryIds
      if (!categoryIds) return [];
      const fetchedCategories = await getCategories({ categoryIds, locale });
      return fetchedCategories ?? [];
    },
    enabled: !!jwt && !!subprofileToken,
  });

  const invalidateCacheIfTimedOut = useCallback(
    async (
      key: string,
      timeout: number,
      queryClient: ReturnType<typeof useQueryClient>,
    ) => {
      if (typeof localStorage === 'undefined') return;
      const now = Date.now();
      const pendingOperations = JSON.parse(
        localStorage.getItem(key) ?? '{}',
      ) as Record<string, number>;

      const operationTimedOut = Object.keys(pendingOperations).some(
        (id) => pendingOperations[id] && now - pendingOperations[id] > timeout,
      );
      if (operationTimedOut) {
        localStorage.setItem(key, JSON.stringify({}));
        await queryClient.invalidateQueries({
          queryKey:
            key === PENDING_ASSET_OPERATIONS
              ? assetsCacheKey
              : categoriesCacheKey,
        });
      }
    },
    [assetsCacheKey, categoriesCacheKey],
  );

  // Invalidate watchList cache when subProfileToken changes or operations timeout
  useEffect(() => {
    if (subProfileTokenRef.current !== subprofileToken) {
      void queryClient.invalidateQueries({ queryKey: ['watchlist'] });
      subProfileTokenRef.current = subprofileToken;
    }
  }, [subprofileToken, queryClient]);

  useEffect(() => {
    const invalidateCacheOnOperationTimedOut = () => {
      void invalidateCacheIfTimedOut(
        PENDING_ASSET_OPERATIONS,
        OPERATION_TIMEOUT,
        queryClient,
      );
      void invalidateCacheIfTimedOut(
        PENDING_CATEGORY_OPERATIONS,
        OPERATION_TIMEOUT,
        queryClient,
      );
    };
    const intervalId = setInterval(invalidateCacheOnOperationTimedOut, 5000);
    return () => clearInterval(intervalId);
  }, [queryClient, invalidateCacheIfTimedOut]);

  const storePendingOperations = (key: string, id: string) => {
    const pendingOperations = JSON.parse(
      localStorage.getItem(key) ?? '{}',
    ) as Record<string, number>;
    pendingOperations[id] = Date.now();
    localStorage.setItem(key, JSON.stringify(pendingOperations));
  };

  const removeStoredPendingOperation = (key: string, id?: string) => {
    if (!id) return;
    const pendingOperations = JSON.parse(
      localStorage.getItem(key) ?? '{}',
    ) as Record<string, number>;
    delete pendingOperations[id];
    localStorage.setItem(key, JSON.stringify(pendingOperations));
  };

  const addToWatchlistMutation = useMutation({
    mutationFn: async ({
      asset,
      category,
    }: {
      asset?: AssetType | CuratedAssetType;
      category?: CategoryType | CuratedCategoryType;
    }) => {
      if (!jwt || !subprofileToken) return;
      if (asset?.id) {
        return await addAssetToWatchlist({
          jwt,
          subprofileToken,
          assetId: asset.id,
        });
      }
      if (category?.id) {
        return await addCategoryToWatchlist({
          jwt,
          subprofileToken,
          categoryId: category.id,
        });
      }
      return;
    },
    onMutate: async ({ asset, category }) => {
      // optimistic update: add to cache, replace if already exits
      if (asset?.id) {
        await queryClient.cancelQueries({ queryKey: assetsCacheKey });
        const previousAssets =
          queryClient.getQueryData<AssetType[]>(assetsCacheKey) ?? [];
        const updatedAssets = [
          asset,
          ...previousAssets.filter((item) => item.id !== asset.id),
        ];
        queryClient.setQueryData(assetsCacheKey, updatedAssets);
        storePendingOperations(PENDING_ASSET_OPERATIONS, asset.id);
        return { previousAssets };
      }
      if (category?.id) {
        await queryClient.cancelQueries({ queryKey: categoriesCacheKey });
        const previousCategories =
          queryClient.getQueryData<CategoryType[]>(categoriesCacheKey) ?? [];
        const updatedCategories = [
          category,
          ...previousCategories.filter((item) => item.id !== category.id),
        ];
        queryClient.setQueryData(categoriesCacheKey, updatedCategories);
        storePendingOperations(PENDING_CATEGORY_OPERATIONS, category.id);
        return { previousCategories };
      }
      return {};
    },
    onError: (_error, _variables, context) => {
      if (context?.previousAssets) {
        queryClient.setQueryData(assetsCacheKey, context.previousAssets);
      }
      if (context?.previousCategories) {
        queryClient.setQueryData(
          categoriesCacheKey,
          context.previousCategories,
        );
      }
    },
    onSettled: (_data, _error, { asset, category }) => {
      if (asset?.id) {
        removeStoredPendingOperation(PENDING_ASSET_OPERATIONS, asset.id);
      }
      if (category?.id) {
        removeStoredPendingOperation(PENDING_CATEGORY_OPERATIONS, category.id);
      }
    },
  });

  const removeFromWatchlistMutation = useMutation({
    mutationFn: async ({
      asset,
      category,
    }: {
      asset?: AssetType | CuratedAssetType;
      category?: CategoryType | CuratedCategoryType;
    }) => {
      if (!jwt || !subprofileToken) return;
      if (asset?.id) {
        await removeAssetFromWatchlist({
          jwt,
          subprofileToken,
          assetId: asset.id,
        });
      }
      if (category?.id) {
        await removeCategoryFromWatchlist({
          jwt,
          subprofileToken,
          categoryId: category.id,
        });
      }
    },
    onMutate: async ({ asset, category }) => {
      // optimistic update: remove from cache
      if (asset?.id) {
        await queryClient.cancelQueries({ queryKey: assetsCacheKey });
        const previousAssets =
          queryClient.getQueryData<AssetType[]>(assetsCacheKey) ?? [];
        const updatedAssets = previousAssets.filter(
          (item) => item.id !== asset.id,
        );
        queryClient.setQueryData(assetsCacheKey, updatedAssets);
        storePendingOperations(PENDING_ASSET_OPERATIONS, asset.id);
        return { previousAssets };
      }
      if (category?.id) {
        await queryClient.cancelQueries({ queryKey: categoriesCacheKey });
        const previousCategories =
          queryClient.getQueryData<CategoryType[]>(categoriesCacheKey) ?? [];
        const updatedCategories = previousCategories.filter(
          (item) => item.id !== category.id,
        );
        queryClient.setQueryData(categoriesCacheKey, updatedCategories);
        storePendingOperations(PENDING_CATEGORY_OPERATIONS, category.id);
        return { previousCategories };
      }
      return {};
    },
    onError: (_error, _variables, context) => {
      // optimistic update rollback on error
      if (context?.previousAssets) {
        queryClient.setQueryData(assetsCacheKey, context.previousAssets);
      }
      if (context?.previousCategories) {
        queryClient.setQueryData(
          categoriesCacheKey,
          context.previousCategories,
        );
      }
    },
    onSettled: (_data, _error, { asset, category }) => {
      if (asset?.id) {
        removeStoredPendingOperation(PENDING_ASSET_OPERATIONS, asset.id);
      }
      if (category?.id) {
        removeStoredPendingOperation(PENDING_CATEGORY_OPERATIONS, category.id);
      }
    },
  });

  const isInWatchlist = ({
    assetId,
    categoryId,
  }: {
    assetId?: string;
    categoryId?: string;
  }) => {
    if (assetId) {
      const watchlist = queryClient.getQueryData<AssetType[]>(assetsCacheKey)!;
      if (!watchlist) return false;
      if (assetId) return watchlist.some((asset) => asset?.id === assetId);
    } else if (categoryId) {
      const watchlist =
        queryClient.getQueryData<CategoryType[]>(categoriesCacheKey)!;
      if (!watchlist) return false;
      if (categoryId)
        return watchlist.some((category) => category?.id === categoryId);
    }
    return false;
  };

  return {
    assets,
    categories,
    isLoading: assetsAreLoading || categoriesAreLoading,
    isInWatchlist,
    addToWatchlist: addToWatchlistMutation.mutateAsync,
    removeFromWatchlist: removeFromWatchlistMutation.mutateAsync,
    addToWatchlistStatus: addToWatchlistMutation.status,
    removeFromWatchlistStatus: removeFromWatchlistMutation.status,
  };
};
